import { useEffect, useMemo, useState } from 'react';
import dayJs from 'dayjs';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { Form, Formik, FormikHelpers } from 'formik';
import { Autocomplete, Box, Button, Collapse, Divider, IconButton, Paper, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { appConfig } from '@app/app';
import { AutoSave, Checkbox, Text, yup } from '@app/formik';
import { Alert } from '@app/ui/alert';
import { Text as MuiText } from '@app/ui/forms';
import { Loader } from '@app/ui/loader';
import { Tooltip } from '@app/ui/tooltip';
import { error as toastError, success as toastSuccess } from '@app/snackbars';

import {
  FIELD_CATALOGUE_GET_MANY,
  RULES_FAMILY_SUMMARY_GET_MANY,
  RULE_GET_ONE,
  RULE_SET_VERSION_GET_MANY,
  RULE_SET_VERSION_GET_ONE,
  RULE_UPSERT_ONE,
} from '../gql';
import {
  ConditionFormValue,
  Rule,
  RuleField,
  RuleLogic,
  RuleSetStatus,
  RuleSetVersion,
  RuleType,
  RuleUpsertInput,
  RulesFamilySummary,
  SeverityOverrideFormValue,
} from '../types';
import {
  FAMILY_SLUGS,
  LOGIC_VALUES,
  NEW_RULE_SEGMENT,
  RULE_TYPE_VALUES,
  SEVERITY_VALUES,
  SLUG_FAMILIES,
} from '../constants';
import {
  collectSections,
  conditionRowError,
  emptyConditionRow,
  getGraphQLErrorMessage,
  nextRowUid,
  toConditionInput,
  toConditionRow,
  toOverrideRow,
  Translate,
} from '../utils';
import { ConditionListEditor } from './ConditionListEditor';
import { RuleTestPanel } from './RuleTestPanel';

interface RuleEditorFormValues {
  type: RuleType;
  ruleText: string;
  severity: string;
  enabled: boolean;
  uiVisible: boolean;
  section: string;
  logic: RuleLogic;
  conditions: ConditionFormValue[];
  evidenceFields: string[];
  severityOverrides: SeverityOverrideFormValue[];
}

const NO_FIELDS: RuleField[] = [];

const buildInitialValues = (rule: Rule | null): RuleEditorFormValues => {
  if (!rule) {
    return {
      type: RuleType.exact,
      ruleText: '',
      severity: 'medium',
      enabled: true,
      uiVisible: true,
      section: '',
      logic: RuleLogic.any,
      conditions: [emptyConditionRow()],
      evidenceFields: [],
      severityOverrides: [],
    };
  }

  return {
    type: rule.type,
    ruleText: rule.ruleText,
    severity: rule.severity,
    enabled: rule.enabled,
    uiVisible: rule.uiVisible,
    section: rule.section ?? '',
    logic: rule.logic ?? RuleLogic.any,
    conditions: (rule.conditions ?? []).map(toConditionRow),
    evidenceFields: rule.evidenceFields ?? [],
    severityOverrides: (rule.severityOverrides ?? []).map(toOverrideRow),
  };
};

// `useBlocker` is unavailable (the app mounts `<BrowserRouter>`, not the data router). Autosave
// covers in-app navigation; this covers an edit inside the debounce followed by a hard reload.
const BeforeUnloadGuard = ({ dirty }: { dirty: boolean }) => {
  useEffect(() => {
    if (!dirty) {
      return undefined;
    }

    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign -- required by the beforeunload spec; some
      // browsers show their own generic prompt only when `returnValue` is set.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);

    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  return null;
};

export const RuleEditor = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const { family: familySlug, ruleId } = useParams<{ family: string; ruleId: string }>();

  const family = familySlug ? SLUG_FAMILIES[familySlug] : undefined;
  const isNew = ruleId === NEW_RULE_SEGMENT;
  const id = isNew ? 0 : Number(ruleId);

  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [overridesOpen, setOverridesOpen] = useState(false);

  const translate: Translate = key => t(key) ?? key;

  const { data: ruleData, loading: ruleLoading } = useQuery<{ ruleGetOne: Rule | null }>(RULE_GET_ONE, {
    variables: { id },
    skip: isNew || !id,
  });

  const rule = ruleData?.ruleGetOne ?? null;

  const { data: versionsData, loading: versionsLoading } = useQuery<{ ruleSetVersionGetMany: RuleSetVersion[] }>(
    RULE_SET_VERSION_GET_MANY,
    { variables: { family }, skip: !isNew || !family },
  );

  const draftVersion = versionsData?.ruleSetVersionGetMany.find(entry => entry.status === RuleSetStatus.draft) ?? null;
  const targetVersionId = isNew ? draftVersion?.id ?? null : rule?.ruleSetVersionId ?? null;

  const { data: versionData, loading: versionLoading } = useQuery<{ ruleSetVersionGetOne: RuleSetVersion | null }>(
    RULE_SET_VERSION_GET_ONE,
    { variables: { id: targetVersionId }, skip: !targetVersionId },
  );

  const version = versionData?.ruleSetVersionGetOne ?? null;

  // `cache-first`: the panel this editor is reached from already ran this same unfiltered query
  // (`cache-and-network`), so a second network round trip here would only re-fetch what the cache
  // already holds. The panel's own refetch is what keeps it fresh.
  const { data: summaryData, loading: summaryLoading } = useQuery<{
    rulesFamilySummaryGetMany: RulesFamilySummary[];
  }>(RULES_FAMILY_SUMMARY_GET_MANY, { fetchPolicy: 'cache-first' });
  // Fails CLOSED, like the panel: only an explicit `true` enables the form, so a failed summary
  // query cannot present editable controls whose every save 400s.
  const familyEditable =
    summaryData?.rulesFamilySummaryGetMany.find(entry => entry.family === family)?.editable === true;

  const editable = version?.status === RuleSetStatus.draft && familyEditable;

  const { data: catalogueData, loading: catalogueLoading } = useQuery<{ fieldCatalogueGetMany: RuleField[] }>(
    FIELD_CATALOGUE_GET_MANY,
    { variables: { family }, skip: !family },
  );

  const fields = useMemo(
    () =>
      [...(catalogueData?.fieldCatalogueGetMany ?? NO_FIELDS)].sort(
        (a, b) => a.appliesTo.localeCompare(b.appliesTo) || a.label.localeCompare(b.label),
      ),
    [catalogueData],
  );
  const fieldsByPath = useMemo(() => new Map(fields.map(field => [field.path, field])), [fields]);

  const sectionOptions = useMemo(() => collectSections(version?.rules ?? []), [version]);

  const [ruleUpsertOne] = useMutation<{ ruleUpsertOne: Rule }>(RULE_UPSERT_ONE, {
    refetchQueries: ['ruleSetVersionGetOne', 'rulesFamilySummaryGetMany'],
  });

  // Keyed to a rule, not the screen: a stale "All changes saved" stamp must not follow the reader
  // to a different rule.
  useEffect(() => {
    setSavedAt(null);
    setSaveError(null);
  }, [rule?.id, isNew]);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        ruleText: yup.string().required(),
        severity: yup.string().required(),
        conditions: yup.array().test({
          name: 'conditionRows',
          test(rows, context) {
            const values = context.parent as RuleEditorFormValues;

            if (values.type === RuleType.ai) {
              return true;
            }

            const conditions = (rows ?? []) as ConditionFormValue[];

            // Only an ENABLED exact rule needs a condition — the server accepts a disabled one
            // with none, and requiring one regardless leaves the form permanently invalid.
            if (!conditions.length) {
              return values.enabled === false
                ? true
                : context.createError({ message: translate('rules.editor.validation.conditionsRequired') });
            }

            return conditions.every(row => !conditionRowError(row, fieldsByPath, translate))
              ? true
              : context.createError({ message: translate('rules.editor.validation.conditionInvalid') });
          },
        }),
        severityOverrides: yup.array().test({
          name: 'overrideRows',
          test(rows, context) {
            const overrides = (rows ?? []) as SeverityOverrideFormValue[];

            const valid = overrides.every(
              override =>
                !!override.severity &&
                override.conditions.length > 0 &&
                override.conditions.every(row => !conditionRowError(row, fieldsByPath, translate)),
            );

            return valid
              ? true
              : context.createError({ message: translate('rules.editor.validation.overrideInvalid') });
          },
        }),
      }),
    [fieldsByPath, t],
  );

  // Keyed on the rule's IDENTITY, not the rule object: a refetch hands back a new object for the
  // same row, and rebuilding `initialValues` would reset the form mid-autosave-debounce.
  const initialValues = useMemo(() => buildInitialValues(rule), [rule?.id, isNew]);

  const fromSearch = (location.state as { fromSearch?: string } | null)?.fromSearch ?? '';
  const backHref = `${family ? `/admin/settings/${FAMILY_SLUGS[family]}` : '/admin/settings'}${fromSearch}`;

  const handleSubmit = async (values: RuleEditorFormValues, helpers: FormikHelpers<RuleEditorFormValues>) => {
    if (!targetVersionId) {
      return;
    }

    const isExact = values.type === RuleType.exact;
    const input: RuleUpsertInput = {
      ...(rule ? { id: rule.id } : {}),
      ruleSetVersionId: targetVersionId,
      type: values.type,
      ruleText: values.ruleText.trim(),
      severity: values.severity,
      enabled: values.enabled,
      uiVisible: values.uiVisible,
      section: values.section.trim() || undefined,
      ...(isExact
        ? {
            logic: values.logic,
            conditions: values.conditions
              .map(row => toConditionInput(row, fieldsByPath))
              .filter((condition): condition is NonNullable<typeof condition> => !!condition),
            evidenceFields: values.evidenceFields,
            // Omitted rather than sent as `[]`: the service maps an absent value to `Prisma.DbNull`,
            // so an empty array would flip a NULL column to `[]` on the first edit.
            ...(values.severityOverrides.length
              ? {
                  severityOverrides: values.severityOverrides.map(override => ({
                    logic: override.logic,
                    severity: override.severity,
                    conditions: override.conditions
                      .map(row => toConditionInput(row, fieldsByPath))
                      .filter((condition): condition is NonNullable<typeof condition> => !!condition),
                  })),
                }
              : {}),
          }
        : {}),
    };

    try {
      const { data } = await ruleUpsertOne({ variables: { input } });

      // Create is an explicit Save — there is no row to autosave onto yet.
      if (!rule && data?.ruleUpsertOne) {
        toastSuccess(t('rules.editor.createdMessage', { ruleId: data.ruleUpsertOne.ruleId }));
        navigate(`/admin/settings/${familySlug}/rule/${data.ruleUpsertOne.id}`, {
          replace: true,
          state: { fromSearch },
        });

        return;
      }

      // Passing `values` is load-bearing: a bare `resetForm()` reverts to the memoised pre-edit
      // `initialValues` and hands `<AutoSave/>` a new reference, resubmitting forever.
      helpers.resetForm({ values });
      setSavedAt(new Date());
      setSaveError(null);
    } catch (e) {
      const message = getGraphQLErrorMessage(e) ?? t('general.error.somethingWentWrong') ?? '';

      setSaveError(message);
      toastError(message);
      helpers.setSubmitting(false);
    }
  };

  // `summaryLoading` too: `editable` fails closed, so rendering before the summary lands would show
  // the read-only notice for a beat on a perfectly editable draft.
  if (ruleLoading || versionsLoading || versionLoading || catalogueLoading || summaryLoading) {
    return <Loader isLoading />;
  }

  if (!isNew && !rule) {
    return (
      <Paper variant="outlined" elevation={0} sx={{ borderRadius: '0 16px 16px 16px', p: { xs: 2, sm: 3, lg: 4 } }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backHref)} sx={{ mb: 2, ml: -1 }}>
          {t('rules.editor.backButton')}
        </Button>
        <Alert severity="error">{t('rules.editor.notFound')}</Alert>
      </Paper>
    );
  }

  if (isNew && !draftVersion) {
    return (
      <Paper variant="outlined" elevation={0} sx={{ borderRadius: '0 16px 16px 16px', p: { xs: 2, sm: 3, lg: 4 } }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backHref)} sx={{ mb: 2, ml: -1 }}>
          {t('rules.editor.backButton')}
        </Button>
        <Alert severity="info">{t('rules.editor.noDraftNotice')}</Alert>
      </Paper>
    );
  }

  return (
    <Box>
      {/* The back link lives INSIDE the card: above it, it landed in the zero-height gap the Tabs'
      `mb: -1px` overlap needs, colliding with the tab row. */}
      <Paper variant="outlined" elevation={0} sx={{ borderRadius: '0 16px 16px 16px', p: { xs: 2, sm: 3, lg: 4 } }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backHref)} sx={{ mb: 2, ml: -1 }}>
          {t('rules.editor.backButton')}
        </Button>

        <Typography variant="h1" sx={{ mb: 1 }}>
          {isNew ? t('rules.editor.newTitle') : t('rules.editor.title', { ruleId: rule?.ruleId })}
        </Typography>

        {!editable && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {/* "Start a draft" is not an option on a family the API refuses to write. */}
            {t(familyEditable ? 'rules.editor.readOnlyVersionNotice' : 'rules.summary.readOnlyFamilyNotice')}
          </Alert>
        )}

        {/* `key`: `enableReinitialize` is deliberately OFF (it would reset the form under an edit
        still inside the autosave debounce), so Formik ignores a changed `initialValues`. Rule→rule
        navigation stays on the same route and React Router keeps this element — without the key the
        next rule opens holding the previous one's answers. */}
        <Formik
          key={isNew ? NEW_RULE_SEGMENT : rule?.id}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ dirty, isSubmitting, isValid, values, errors, submitCount, setFieldValue, submitForm }) => (
            <Form noValidate>
              {editable && !isNew && (
                <>
                  <AutoSave />
                  <BeforeUnloadGuard dirty={dirty} />

                  {/* Reserve the row's height so the indicator appearing never reflows the form. A
                  failed save wins over a stale `savedAt`: the user must never see a success stamp
                  for a write that did not happen. */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', minHeight: 24, mb: 1 }}>
                    <Typography
                      variant="body2"
                      color={!isSubmitting && (saveError || !isValid) ? 'error' : 'text.secondary'}
                    >
                      {isSubmitting && t('rules.editor.autosave.saving')}
                      {!isSubmitting && saveError && t('rules.editor.autosave.failed', { message: saveError })}
                      {!isSubmitting && !saveError && !isValid && t('rules.editor.autosave.validationBlocked')}
                      {!isSubmitting &&
                        !saveError &&
                        isValid &&
                        savedAt &&
                        t('rules.editor.autosave.savedAt', {
                          time: dayJs(savedAt).format(appConfig.format.time),
                        })}
                    </Typography>
                  </Box>
                </>
              )}

              <Text
                disabled={!editable}
                name="ruleText"
                label={t('rules.editor.field.ruleText.label')}
                helperText={t('rules.editor.field.ruleText.helperText')}
                placeholder={t('rules.editor.field.ruleText.placeholder') ?? undefined}
                multiline
                rows={2}
                sx={{ mb: 2 }}
              />

              {/* Imported commentary — read-only, the editor never writes it. */}
              {!!rule?.note && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h4" sx={{ mb: 0.5 }}>
                    {t('rules.editor.sourceNotes.title')}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {rule.note}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2, mb: 3 }}>
                <Box sx={{ width: { xs: '100%', sm: 200 } }}>
                  <Text
                    select
                    disabled={!editable}
                    name="type"
                    label={t('rules.editor.field.type.label')}
                    options={RULE_TYPE_VALUES.map(value => ({
                      id: value,
                      name: t(`rules.type.option.${value}`) ?? '',
                    }))}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 200 } }}>
                  <Text
                    select
                    disabled={!editable}
                    name="severity"
                    label={t('rules.editor.field.severity.label')}
                    options={SEVERITY_VALUES.map(value => ({
                      id: value,
                      name: t(`rules.severity.option.${value}`) ?? '',
                    }))}
                  />
                </Box>

                {/* `alignItems: flex-end` on the row: the default `stretch` makes each Checkbox's
                FormControl a full-height column, parking its label level with the select's LABEL. */}
                <Checkbox disabled={!editable} name="enabled" label={t('rules.editor.field.enabled.label')} />
                <Checkbox disabled={!editable} name="uiVisible" label={t('rules.editor.field.uiVisible.label')} />
              </Box>

              <Autocomplete
                freeSolo
                disabled={!editable}
                options={sectionOptions}
                value={values.section}
                inputValue={values.section}
                onChange={(_event, next) => setFieldValue('section', next ?? '')}
                onInputChange={(_event, next) => setFieldValue('section', next)}
                renderInput={params => (
                  <MuiText
                    {...params}
                    label={t('rules.editor.field.section.label')}
                    helperText={t('rules.editor.field.section.helperText')}
                  />
                )}
                sx={{ mb: 3 }}
              />

              {values.type === RuleType.exact && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="h2" sx={{ mb: 1 }}>
                    {t('rules.editor.section.conditions')}
                  </Typography>

                  {/* Wide enough to keep the helper text on one line — it measures 297px at the
                  theme's 11px Open Sans. */}
                  <Box sx={theme => ({ width: { xs: '100%', sm: theme.spacing(40) }, mb: 2 })}>
                    <Text
                      select
                      disabled={!editable}
                      name="logic"
                      label={t('rules.editor.field.logic.label')}
                      helperText={t('rules.editor.field.logic.helperText')}
                      options={LOGIC_VALUES.map(value => ({ id: value, name: t(`rules.logic.option.${value}`) ?? '' }))}
                    />
                  </Box>

                  <ConditionListEditor
                    name="conditions"
                    fields={fields}
                    fieldsByPath={fieldsByPath}
                    disabled={!editable}
                    allowEmpty={!values.enabled}
                    emptyMessage={t('rules.editor.condition.empty') ?? ''}
                  />

                  {submitCount > 0 && !!errors.conditions && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      {String(errors.conditions)}
                    </Typography>
                  )}

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {t('rules.editor.evidence.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('rules.editor.evidence.intro')}
                  </Typography>
                  <Autocomplete
                    multiple
                    disabled={!editable}
                    options={fields}
                    value={values.evidenceFields
                      .map(path => fieldsByPath.get(path))
                      .filter((field): field is RuleField => !!field)}
                    getOptionLabel={option => option.label}
                    isOptionEqualToValue={(option, selected) => option.path === selected.path}
                    onChange={(_event, next) =>
                      setFieldValue(
                        'evidenceFields',
                        next.map(field => field.path),
                      )
                    }
                    renderOption={(props, option) => (
                      <li {...props} key={option.path}>
                        {option.label}
                      </li>
                    )}
                    renderInput={params => (
                      <MuiText
                        {...params}
                        label={t('rules.editor.evidence.label')}
                        helperText={t('rules.editor.evidence.helperText')}
                      />
                    )}
                    sx={{ mb: 3 }}
                  />

                  {/* An ordered list, first match wins; the section itself is rendered
                  only for a rule that has at least one override, so the other 91 look unchanged. */}
                  {values.severityOverrides.length > 0 && (
                    <>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h4">
                          {t('rules.editor.overrides.title', { count: values.severityOverrides.length })}
                        </Typography>
                        <Button size="small" onClick={() => setOverridesOpen(previous => !previous)}>
                          {overridesOpen
                            ? t('rules.editor.overrides.hideButton')
                            : t('rules.editor.overrides.showButton')}
                        </Button>
                      </Box>

                      <Collapse in={overridesOpen} unmountOnExit>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {t('rules.editor.overrides.intro')}
                        </Typography>

                        {values.severityOverrides.map((override, index) => (
                          <Box
                            key={override.uid}
                            sx={{ border: '1px solid', borderColor: 'grey.400', borderRadius: '16px', p: 2, mb: 2 }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="h4">
                                {t('rules.editor.overrides.heading', { number: index + 1 })}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title={t('rules.editor.overrides.moveUpButton') ?? undefined}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={!editable || index === 0}
                                      onClick={() => {
                                        const next = [...values.severityOverrides];

                                        [next[index - 1], next[index]] = [next[index], next[index - 1]];
                                        setFieldValue('severityOverrides', next);
                                      }}
                                    >
                                      <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title={t('rules.editor.overrides.moveDownButton') ?? undefined}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={!editable || index === values.severityOverrides.length - 1}
                                      onClick={() => {
                                        const next = [...values.severityOverrides];

                                        [next[index], next[index + 1]] = [next[index + 1], next[index]];
                                        setFieldValue('severityOverrides', next);
                                      }}
                                    >
                                      <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title={t('rules.editor.overrides.removeButton') ?? undefined}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={!editable}
                                      onClick={() =>
                                        setFieldValue(
                                          'severityOverrides',
                                          values.severityOverrides.filter((_row, rowIndex) => rowIndex !== index),
                                        )
                                      }
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </Box>

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                              <Box sx={{ width: { xs: '100%', sm: 200 } }}>
                                <Text
                                  select
                                  disabled={!editable}
                                  name={`severityOverrides.${index}.severity`}
                                  label={t('rules.editor.overrides.severityLabel')}
                                  options={SEVERITY_VALUES.map(value => ({
                                    id: value,
                                    name: t(`rules.severity.option.${value}`) ?? '',
                                  }))}
                                />
                              </Box>
                              <Box sx={{ width: { xs: '100%', sm: 200 } }}>
                                <Text
                                  select
                                  disabled={!editable}
                                  name={`severityOverrides.${index}.logic`}
                                  label={t('rules.editor.field.logic.label')}
                                  options={LOGIC_VALUES.map(value => ({
                                    id: value,
                                    name: t(`rules.logic.option.${value}`) ?? '',
                                  }))}
                                />
                              </Box>
                            </Box>

                            <ConditionListEditor
                              name={`severityOverrides.${index}.conditions`}
                              fields={fields}
                              fieldsByPath={fieldsByPath}
                              disabled={!editable}
                              emptyMessage={t('rules.editor.overrides.emptyConditions') ?? ''}
                            />
                          </Box>
                        ))}

                        {submitCount > 0 && !!errors.severityOverrides && (
                          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {String(errors.severityOverrides)}
                          </Typography>
                        )}
                      </Collapse>
                    </>
                  )}

                  {editable && (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setOverridesOpen(true);
                        setFieldValue('severityOverrides', [
                          ...values.severityOverrides,
                          {
                            uid: nextRowUid('override'),
                            logic: RuleLogic.all,
                            severity: 'medium',
                            conditions: [emptyConditionRow()],
                          },
                        ]);
                      }}
                    >
                      {t('rules.editor.overrides.addButton')}
                    </Button>
                  )}
                </>
              )}

              {isNew && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
                  <Button onClick={() => navigate(backHref)}>{t('rules.editor.cancelButton')}</Button>
                  <LoadingButton loading={isSubmitting} variant="contained" color="primary" onClick={submitForm}>
                    {t('rules.editor.createButton')}
                  </LoadingButton>
                </Box>
              )}
            </Form>
          )}
        </Formik>
      </Paper>

      {/* `startTestRun` rejects a non-draft row with a 400, so on a
      published version the panel could only offer a button that fails. `key`: the panel holds the
      run in local state, and rule→rule navigation reuses this component instance. */}
      {!editable && (
        <Alert severity="info" sx={{ mt: 3 }}>
          {t('rules.test.readOnlyVersionNotice')}
        </Alert>
      )}

      {editable &&
        (rule ? (
          <RuleTestPanel key={rule.id} ruleRowId={rule.id} ruleId={rule.ruleId} />
        ) : (
          <Alert severity="info" sx={{ mt: 3 }}>
            {t('rules.test.saveFirstNotice')}
          </Alert>
        ))}
    </Box>
  );
};
